import React, { useEffect, useMemo, useState } from 'react';
import * as yup from 'yup';
import moment from 'moment';
import { SUPPORTED_LEAGUES, SxStyle } from '../../../../../types';
import { useStringsContext } from '../../../../../contexts/strings';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { query, limit, where, collection, orderBy, getDocs } from 'firebase/firestore';
import firebaseApp from '../../../../../firebase-app';
import { getTeamMatches } from '../../../../../utils/paths';
import { useSnackbar } from 'notistack';
import { MatchMinimized } from '../../../matches/utils/data';
import { useSquad } from '../../../squad-database/hooks/squad';
import { PlayerPoll, PlayerPollKeys, PlayerPollOption } from '../../types';
import PollCard from '../poll-card';
import PollPlayerToggle from '../poll-player-toggle';
import TextInputWithValidate, {
  InputWithValidateProps,
} from '../../../../common/input/text-input-with-validate';
import SelectWithValidate, { SelectOptions } from '../../../../common/input/select-with-validate';
import MultipleSelectWithValidate from '../../../../common/input/multiple-select-with-validate';
import { Option } from '../../../../../types';
import PollSelectedPlayersLeague from '../poll-select-players-league';

const { REACT_APP_DEFAULT_LEAGUE_SEASON } = process.env;

const titleKey: PlayerPollKeys = 'title';
const matchIdKey: PlayerPollKeys = 'matchId';
const configLeagueNameKey: PlayerPollKeys = 'configLeagueName';
const configSeasonStartYearKey: PlayerPollKeys = 'configSeasonStartYear';
const squadPlayersKey = 'squadPlayers';
const pollOptionsKey = 'pollOptions';

const styles: SxStyle = {
  inputTitle: {
    display: 'block',
    marginBottom: '8px',
  },
};

interface Props extends InputWithValidateProps {
  selectedPoll: PlayerPoll | null;
  selectedAbbrv: string;
  onDelete: (fieldKey: string) => void;
}

const PollPlayerDetailsCard = ({
  selectedPoll,
  selectedAbbrv,
  onValue,
  onValidation,
  onDelete,
  hideValidationError,
}: Props) => {
  const { strings } = useStringsContext();
  const { enqueueSnackbar } = useSnackbar();
  const { fetchSquad } = useSquad();

  const isLeague = selectedAbbrv ? selectedAbbrv in SUPPORTED_LEAGUES : false;

  const [selectedPlayerSelection, setSelectedPlayerSelection] = useState<'match' | 'squad'>(
    selectedPoll ? (selectedPoll.matchId ? 'match' : 'squad') : 'match'
  );
  const [matches, setMatches] = useState<MatchMinimized[] | null>(null);

  const [matchesOptions, setMatchesOptions] = useState<SelectOptions>({
    options: [],
    optionsText: [],
  });

  const [playersOptions, setPlayersOptions] = useState<SelectOptions>({
    options: [],
    optionsText: [],
  });

  const initialSelectedPlayers = useMemo(() => {
    const newSelectedPlayersOptions = selectedPoll?.pollOptions?.map((option) =>
      String(option.fogisId)
    );
    const newSelectedPlayersOptionsText = selectedPoll?.pollOptions?.map(
      (option) => option.text || ''
    );

    return {
      options: newSelectedPlayersOptions || [],
      optionsText: newSelectedPlayersOptionsText || [],
    };
  }, [selectedPoll?.pollOptions]);

  useEffect(() => {
    if (!isLeague && selectedPlayerSelection === 'match') {
      const getMatchDocs = async () => {
        try {
          const path = getTeamMatches(selectedAbbrv);
          const colRef = collection(firebaseApp.db, path);
          const matchQuery = query(
            colRef,
            where('startDate', '>=', moment().startOf('day').toISOString()),
            orderBy('startDate', 'asc'),
            limit(10)
          );
          const { docs: matchDocs } = await getDocs(matchQuery);
          const matches = matchDocs?.map((doc) => doc.data()) as MatchMinimized[];

          setMatches(matches);

          const matchesSorted = matches?.sort(
            (a, b) => moment(a.startDate).valueOf() - moment(b.startDate).valueOf()
          );
          setMatchesOptions({
            options: matchesSorted?.map((match) => match.id),
            optionsText: matchesSorted?.map(
              (match) =>
                `(${moment(match.startDate).format('YYYY-MM-DD hh:mm')}) ${
                  match.homeTeamName
                } - ${match.visitingTeamName}`
            ),
          });
        } catch (error) {
          console.log(error);
          enqueueSnackbar(strings.messages.loadingError, {
            variant: 'error',
          });
        }
      };
      getMatchDocs();
    }
  }, [enqueueSnackbar, selectedPlayerSelection, selectedPoll, selectedAbbrv, strings, isLeague]);

  useEffect(() => {
    const getSquad = async () => {
      if (!isLeague && !playersOptions.options.length) {
        try {
          const playerSquad = await fetchSquad(
            selectedAbbrv,
            Number(REACT_APP_DEFAULT_LEAGUE_SEASON),
            false,
            false
          );

          if (playerSquad && playerSquad.length > 0) {
            setPlayersOptions({
              options: playerSquad.map((player) => String(player.fogisId)),
              optionsText: playerSquad.map(
                (player) => player.displayName || String(player.fogisId)
              ),
            });
          }
        } catch (error) {
          console.error('Error fetching squad players:', error);
        }
      }
    };
    getSquad();
  }, [
    fetchSquad,
    selectedAbbrv,
    selectedPlayerSelection,
    selectedPoll,
    playersOptions.options,
    isLeague,
  ]);

  const onChangeSelection = (type: 'match' | 'squad') => {
    onDelete(type === 'match' ? squadPlayersKey : matchIdKey);
    setSelectedPlayerSelection(type);
  };

  const handleMatchValue = (key: string, value: Option | null) => {
    if (!onValue) return;

    const match = matches?.find((match) => String(match.id) === value);
    if (match) {
      onValue(configLeagueNameKey, match.configLeagueName);
      onValue(configSeasonStartYearKey, match.configSeasonStartYear);
      onValue(key, value);
    }
  };

  const handleSelectedPlayers = (selectedPlayers: SelectOptions | null) => {
    if (!selectedPlayers) return;
    const squadPlayerOptions: PlayerPollOption[] = selectedPlayers.options.map((option, index) => {
      return {
        id: index,
        fogisId: Number(option),
        text: selectedPlayers.optionsText[index],
        type: 'player',
      };
    });
    onValue && onValue(pollOptionsKey, squadPlayerOptions);
  };

  // const handleValueTime = (key: string, value: string | null) => { // TODO useCallback
  //   onValue && onValue(key, moment(value).toISOString());

  //   // TODO Fix set endtime based on starttime
  //   // Set end time to start time + 1 hour (if it's not already set)
  //   // if (fieldKey === 'startTime' && !endTimeField.value) {
  //   //   endTimeField.value = moment(value).add(1, 'hours').format('YYYY-MM-DD HH:mm');
  //   // }
  // }

  // TODO Skip?
  // const handleMatchOptionValue = useCallback((key: string, value: Option | null) => {
  //   onValue && onValue(key, value)
  // }, []);

  return (
    <PollCard title={strings.polls.pollDetailsTitle}>
      <TextInputWithValidate
        name={titleKey}
        validation={yup.string().required(strings.validations.isRequired)}
        label={strings.polls.pollTitleLabel}
        value={selectedPoll?.[titleKey]}
        onValue={onValue}
        onValidation={onValidation}
        hideValidationError={hideValidationError}
      />
      <Stack spacing={2}>
        <Typography sx={styles.inputTitle} variant="body2-bold">
          {strings.polls.playerSelectTitle}
        </Typography>
        {isLeague ? (
          <PollSelectedPlayersLeague
            selectedAbbrv={selectedAbbrv}
            initialSelectedPlayers={initialSelectedPlayers}
            onSelectedPlayers={handleSelectedPlayers}
            onValidation={onValidation}
            hideValidationError={hideValidationError}
          />
        ) : (
          <>
            <PollPlayerToggle
              toggleType="match"
              selectedToggle={selectedPlayerSelection}
              onToggle={onChangeSelection}
            />
            {selectedPlayerSelection === 'match' && (
              <SelectWithValidate
                name={matchIdKey}
                options={matchesOptions.options}
                optionsText={matchesOptions.optionsText}
                validation={yup.string().required(strings.validations.isRequired)}
                label={strings.polls.selectMatchLabel}
                value={selectedPoll?.matchId || ''}
                onValue={handleMatchValue}
                onValidation={onValidation}
                hideValidationError={hideValidationError}
              />
            )}
            <PollPlayerToggle
              toggleType="squad"
              selectedToggle={selectedPlayerSelection}
              onToggle={onChangeSelection}
              disabled={!playersOptions.options.length}
            />
            {selectedPlayerSelection === 'squad' && (
              <MultipleSelectWithValidate
                name={squadPlayersKey}
                options={playersOptions.options}
                optionsText={playersOptions.optionsText}
                validation={yup
                  .array(yup.string())
                  .min(1, strings.validations.mustSelectAtleastOneAlternative)
                  .required(strings.validations.mustSelectAtleastOneAlternative)}
                label={strings.polls.selectPlayersLabel}
                value={selectedPoll?.pollOptions?.map((option) => String(option.fogisId))}
                onValue={onValue}
                onValidation={onValidation}
                hideValidationError={hideValidationError}
              />
            )}
          </>
        )}
      </Stack>
    </PollCard>
  );
};

export default PollPlayerDetailsCard;
